import { createSelector } from 'reselect'
import moment from 'moment'
import config from 'constants/config'
import {
  findPostPurchaseCheckout,
  isInstantBookingLEHotelItem,
  isLEHotelItem,
  isNoProtectionInsuranceItem,
} from 'lib/checkout/checkoutUtils'
import { isGroupPaymentLeadOnlyCartItem } from 'checkout/lib/groupPayment/leadOnlyItems'
import { isLEHotel } from 'lib/offer/offerTypes'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { selectIsGuestCheckoutMode } from 'selectors/checkoutSelectors'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { getCartHasPropertyCollectItem } from '../propertyCollectSelectors'
import {
  GROUP_PAYMENT_FREE_CANCELLATION_BUFFER_DAYS,
  GROUP_PAYMENT_RESIDUAL_DUE_DAYS,
} from 'checkout/lib/groupPayment/groupPaymentDueDate'

const isGroupPaymentEligible = createSelector(
  selectIsGuestCheckoutMode,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.isGift,
  getCartHasPropertyCollectItem,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (
    isGuestCheckoutMode,
    postPurchase,
    isGift,
    hasPropertyCollectItem,
    cartItems,
    offers,
    accommodationOfferView,
    offerAvailableRatesByOccupancy,
  ): boolean => {
    if (!config.GROUP_PAYMENTS_ENABLED || isGuestCheckoutMode || postPurchase || isGift || hasPropertyCollectItem) {
      return false
    }

    // LE hotels are split with the group; lead-only items (LuxPlus, insurance) are paid by the lead.
    const payableItems = cartItems.filter(item => !isNoProtectionInsuranceItem(item))
    if (!payableItems.every(item => isLEHotelItem(item) || isGroupPaymentLeadOnlyCartItem(item))) {
      return false
    }

    const hotelItems = payableItems.filter(isLEHotelItem)
    if (!hotelItems.length) {
      return false
    }

    const offerIds = new Set(hotelItems.map(item => item.offerId))
    if (offerIds.size !== 1) {
      return false
    }

    const offer = offers[hotelItems[0].offerId]
    if (!isLEHotel(offer)) {
      return false
    }

    return accommodationOfferView.hasRequiredData &&
      accommodationOfferView.data.length > 0 &&
      accommodationOfferView.data.every(view => {
        const checkIn = view.startDate ? moment(view.startDate) : undefined
        return view.itemViews.length > 0 && view.itemViews.every(itemView => {
          const pkg = 'pkg' in itemView ? itemView.pkg : undefined
          // Channel-managed LPC rates carry their own free-cancel deadline on the available rate.
          const dynamicCancellationPolicies = 'pkg' in itemView && isInstantBookingLEHotelItem(itemView.item) ?
            offerAvailableRatesByOccupancy[offer.id]?.[
              buildAvailableRateKey(itemView.item.checkIn, itemView.item.checkOut, [itemView.item.occupancy])
            ]?.rates.find(rate => rate.packageUniqueKey === pkg?.uniqueKey)?.cancellationPolicies :
            undefined
          return isPackageEligibleForZeroReservation(
            offer,
            checkIn,
            pkg?.roomRate?.cancellationPolicy?.type,
            dynamicCancellationPolicies,
            pkg?.ignoreDynamicCancellationPolicy,
            GROUP_PAYMENT_RESIDUAL_DUE_DAYS + GROUP_PAYMENT_FREE_CANCELLATION_BUFFER_DAYS,
          )
        })
      })
  },
)

export default isGroupPaymentEligible
